import { useState } from 'react';
import { QrCode, AlertCircle, CheckCircle2, MapPin, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';

export const UserReportPage = () => {
  const [step, setStep] = useState('scan'); // scan, form, success
  const [selectedType, setSelectedType] = useState('');

  const tagInfo = {
    id: 'TAG-00432',
    location: 'Shopping Colombo - Piso -2, Setor B',
    type: 'Posto de Carregamento'
  };

  const incidentTypes = [
    { value: 'avaria', label: 'Carregador Avariado', piket: true, icon: '⚠️' },
    { value: 'cabo_danificado', label: 'Cabo Danificado', piket: true, icon: '🔌' },
    { value: 'falta_energia', label: 'Falta de Energia', piket: true, icon: '⚡' },
    { value: 'carregamento_lento', label: 'Carregamento Lento', piket: false, icon: '🐌' },
    { value: 'erro_pagamento', label: 'Erro no Pagamento', piket: false, icon: '💳' },
    { value: 'posto_ocupado', label: 'Posto Ocupado Incorretamente', piket: false, icon: '🚗' },
    { value: 'limpeza', label: 'Necessita Limpeza', piket: false, icon: '🧹' },
    { value: 'vandalismo', label: 'Vandalismo', piket: true, icon: '🚨' },
    { value: 'outros', label: 'Outros', piket: false, icon: '📝' }
  ];

  const handleSubmit = () => {
    setStep('success');
    // Simular criação de ticket e alerta
    setTimeout(() => {
      // Resetar após 5 segundos
      setTimeout(() => {
        setStep('scan');
        setSelectedType('');
      }, 5000);
    }, 2000);
  };

  const selectedIncident = incidentTypes.find(t => t.value === selectedType);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="max-w-md mx-auto p-6">
        <div className="text-center mb-8 mt-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4">
            <QrCode className="text-white" size={32} />
          </div>
          <h1 className="text-2xl font-secondary font-bold text-slate-900">condutor.online</h1>
          <p className="text-slate-600 text-sm mt-1">Sistema de Alertas</p>
        </div>

        {step === 'scan' && (
          <Card className="p-6 text-center">
            <div className="mb-6">
              <div className="w-48 h-48 mx-auto bg-slate-100 rounded-xl flex items-center justify-center border-2 border-dashed border-slate-300">
                <QrCode className="text-slate-400" size={80} />
              </div>
            </div>
            <h2 className="text-xl font-secondary font-bold text-slate-900 mb-2">
              Escanear QR Code
            </h2>
            <p className="text-slate-600 text-sm mb-6">
              Aproxime a câmera do QR Code no posto de carregamento
            </p>
            <Button 
              data-testid="simulate-scan"
              onClick={() => setStep('form')}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              Simular Escaneamento
            </Button>
          </Card>
        )}

        {step === 'form' && (
          <Card className="p-6">
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-3">
                <MapPin className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-secondary font-bold text-slate-900">{tagInfo.id}</p>
                  <p className="text-sm text-slate-600">{tagInfo.location}</p>
                  <p className="text-xs text-blue-600 mt-1">{tagInfo.type}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="incident-type">Tipo de Ocorrência *</Label>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger id="incident-type">
                    <SelectValue placeholder="Selecione o tipo de problema" />
                  </SelectTrigger>
                  <SelectContent>
                    {incidentTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <span className="flex items-center gap-2">
                          <span>{type.icon}</span>
                          <span>{type.label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedIncident?.piket && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={16} />
                    <div>
                      <p className="text-xs font-bold text-red-900 mb-1">Alerta Prioritário</p>
                      <p className="text-xs text-red-700">
                        Este tipo de ocorrência irá acionar automaticamente o piket de assistência
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="description">Descrição (Opcional)</Label>
                <Textarea 
                  id="description"
                  placeholder="Descreva o problema com mais detalhes..."
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="user-name">Seu Nome (Opcional)</Label>
                <Input
                  id="user-name"
                  placeholder="Ex: João Silva"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="user-email">Email (Opcional)</Label>
                <Input
                  id="user-email"
                  type="email"
                  placeholder="joao@email.com"
                />
                <p className="text-xs text-slate-500">
                  Para receber notificação quando o problema for resolvido
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="user-reference">Referência do Veículo (Opcional)</Label>
                <Input
                  id="user-reference"
                  placeholder="AB-12-CD"
                />
                <p className="text-xs text-slate-500">
                  Ajuda-nos a associar o report ao seu veículo
                </p>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setStep('scan')}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button 
                data-testid="submit-report"
                onClick={handleSubmit}
                disabled={!selectedType}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300"
              >
                Reportar Ocorrência
              </Button>
            </div>
          </Card>
        )}

        {step === 'success' && (
          <Card className="p-6 text-center">
            <div className="mb-6">
              <div className="w-20 h-20 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="text-emerald-600" size={48} />
              </div>
              <h2 className="text-xl font-secondary font-bold text-slate-900 mb-2">
                Ocorrência Reportada!
              </h2>
              <p className="text-slate-600 text-sm mb-4">
                Ticket <span className="font-secondary font-bold text-blue-600">TKT-8945</span> criado com sucesso
              </p>
            </div>

            <div className="space-y-3 mb-6">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 text-left">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="text-blue-600" size={16} />
                  <span className="text-sm font-bold text-blue-900">Ticket Criado</span>
                </div>
                <p className="text-xs text-blue-700">
                  A sua ocorrência foi registada no sistema e será tratada pela nossa equipa
                </p>
              </div>

              {selectedIncident?.piket && (
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200 text-left">
                  <div className="flex items-center gap-2 mb-2">
                    <Phone className="text-emerald-600" size={16} />
                    <span className="text-sm font-bold text-emerald-900">Piket Alertado</span>
                  </div>
                  <p className="text-xs text-emerald-700">
                    O piket de assistência foi notificado e está a caminho
                  </p>
                </div>
              )}
            </div>

            <p className="text-xs text-slate-500 mb-4">
              Obrigado por nos ajudar a manter o serviço de qualidade
            </p>

            <div className="text-xs text-slate-400">
              Redirecionando em 5 segundos...
            </div>
          </Card>
        )}

        <div className="mt-8 text-center">
          <p className="text-xs text-slate-500">
            Em caso de emergência, ligue para <span className="font-bold text-slate-900">112</span>
          </p>
        </div>
      </div>
    </div>
  );
};
